/**
 * <TutorStudentRow> — un élève du masteur, en ligne de « Mes élèves » (organism).
 *
 * Toute la ligne ouvre sa fiche (`StudentsListItem.js`, dont les quatre gestes sont devenus les onglets
 * de la fiche, Wajih, 02/11). Elle dit qui il est et ce qu'il suit avec ce masteur : une matière par
 * ligne, avec sa séance (`<SubjectBadge>`), jamais les matières des autres élèves du cours. Sans cours,
 * ses matières prioritaires, comme l'ancienne ligne.
 *
 * Ses signaux : les activités finies aujourd'hui, sous ses cours, et au bout de la ligne les messages non
 * lus, qu'on touche pour aller droit à la conversation. Les rapports et le jeu sont dans sa fiche.
 *
 * `selected` : sa fiche est ouverte à côté (deux volets). Une ligne, pas une carte : la liste est UNE
 * carte-liste. Un élève porte le rond simple, jamais la capsule du tuteur.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { CardListRow } from "../../molecules/CardList"
import { SubjectBadges } from "../../molecules/SubjectBadges"
import { cappedCount } from "../../utils/cappedCount"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { type UITutorStudent } from "../../../ports/primary/ui/types/tutorEleves"

/** L'avatar de la ligne — le 50 px du legacy, ramené à la grille. */
export const TUTOR_STUDENT_AVATAR_SIZE = 48

export type TutorStudentRowProps = {
    student: UITutorStudent
    /** Toucher la ligne : sa fiche. Absent, elle ne presse pas. */
    onOpenProfile?: () => void
    /** Toucher ses non-lus : sa fiche, sur Messages. */
    onOpenMessages?: () => void
    /** Sa fiche est ouverte à côté. Défaut `false`. */
    selected?: boolean
    testID?: string
}

export function TutorStudentRow(props: TutorStudentRowProps) {
    const { student, onOpenProfile, onOpenMessages, selected = false, testID } = props
    const unread = student.unreadMessagesCount ?? 0
    const done = student.todayActivitiesCount ?? 0
    const courses = student.courses ?? []

    return (
        <View style={selected ? styles.selected : undefined} testID={testID}>
            <Pressable
                onPress={onOpenProfile}
                disabled={onOpenProfile == null}
                accessibilityRole={onOpenProfile != null ? "button" : undefined}
                accessibilityLabel={onOpenProfile != null ? `Ouvrir la fiche de ${student.name}` : undefined}
                accessibilityState={{ selected }}
                style={({ pressed }) => (pressed ? styles.pressed : undefined)}
            >
                <CardListRow>
                    <Row gap="3" align="center" style={unread > 0 ? styles.roomForUnread : undefined}>
                        <Avatar
                            uri={student.avatarUrl}
                            name={student.name}
                            size={TUTOR_STUDENT_AVATAR_SIZE}
                            selected={selected}
                        />
                        <Stack gap="1" style={styles.body}>
                            <Row gap="2" align="center" style={styles.wrap}>
                                <Text size="text-md" weight="bold" color="primary">
                                    {student.name}
                                </Text>
                                {student.classLabel != null && student.classLabel !== "" && (
                                    <Text size="text-sm" color="text-muted">
                                        {student.classLabel}
                                    </Text>
                                )}
                            </Row>
                            {courses.length > 0 ? (
                                <Stack gap="1">
                                    {courses.map((course) => (
                                        <Row key={course.id} gap="2" align="center" style={styles.wrap}>
                                            <SubjectBadge subject={course.subject} showLabel />
                                            <Text size="text-sm" color="text-muted">
                                                {course.shortScheduleLabel}
                                            </Text>
                                            {course.status === "requested" && (
                                                <Text size="text-sm" color="text-warning">
                                                    demandé
                                                </Text>
                                            )}
                                        </Row>
                                    ))}
                                </Stack>
                            ) : (
                                student.subjects.length > 0 && <SubjectBadges subjects={student.subjects} />
                            )}
                            {done > 0 && (
                                <Text size="text-xs" color="text-muted">
                                    {`${countLabel(done, "activité finie", "activités finies")} aujourd'hui`}
                                </Text>
                            )}
                        </Stack>
                    </Row>
                </CardListRow>
            </Pressable>
            {/* À côté de la ligne, pas dedans : un bouton ne se pose pas dans un bouton. */}
            {unread > 0 && (
                <View style={styles.unread} pointerEvents="box-none">
                    <Pressable
                        onPress={onOpenMessages}
                        disabled={onOpenMessages == null}
                        accessibilityRole="button"
                        accessibilityLabel={`Lire les messages de ${student.name}, ${unread} non lus`}
                        hitSlop={8}
                    >
                        <Row gap="1" align="center">
                            <Icon name="comment-dots" size={16} color="primary" />
                            <View>
                                <Badge text={cappedCount(unread)} variant="danger" />
                            </View>
                        </Row>
                    </Pressable>
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    selected: { backgroundColor: theme.colors["primary-soft"] },
    pressed: { backgroundColor: theme.colors["primary-soft"] },
    body: { flex: 1, minWidth: 0 },
    wrap: { flexWrap: "wrap" },
    /** La place des non-lus, posés par-dessus au bout de la ligne. */
    roomForUnread: { paddingRight: 60 },
    unread: {
        position: "absolute",
        top: 0,
        bottom: 0,
        right: theme.spacing["4"],
        justifyContent: "center",
    },
}))
